import { Actions, ofType, createEffect } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { createAction, props, on, createSelector } from '@ngrx/store';
import { EntityStoreFactory, IGenericEntityState } from '@pit/core-store';
import { IDtoShipment } from '@pit/frontend-model';
import { EntityDataServiceAccessorService } from '@pit/core-data';
import { IDtoShipmentEntityDataService } from '../../module.data/services/interfaces/dto.shipment.entity.data.service.interface';

export class DtoShipmentStoreFactory extends EntityStoreFactory<IDtoShipment> {

    private static _instance: DtoShipmentStoreFactory = null;

    public static get instance(): DtoShipmentStoreFactory {
        if (this._instance === null) {
            this._instance = new DtoShipmentStoreFactory();
        }

        return this._instance;
    }

    constructor() {
        super('DtoShipment', 'dto.shipment');
    }

    protected override doCreateActions(): void {
        super.doCreateActions();

        this.entityActions.add('get.by.date', createAction(
            'Get Shipments By Date',
            props<{ date: Date }>()
        ));
        this.entityActions.add('get.by.date.success', createAction(
            'Get Shipments By Date Success',
            props<{ items: IDtoShipment[] }>()
        ));
        this.entityActions.add('get.by.date.failure', createAction(
            'Get Shipments By Date Failure',
            props<{ error: any }>()
        ));

        this.entityActions.add('get.active.by.user', createAction(
            'Get Active Shipments By User',
            props<{ userName: string }>()
        ));
        this.entityActions.add('get.active.by.user.success', createAction(
            'Get Active Shipments By User Success',
            props<{ items: IDtoShipment[] }>()
        ));
        this.entityActions.add('get.active.by.user.failure', createAction(
            'Get Active Shipments By User Failure',
            props<{ error: any }>()
        ));

        this.entityActions.add('get.active.by.depot', createAction(
            'Get Active Shipments For Depot',
            props<{ depotNumber: string }>()
        ));
        this.entityActions.add('get.active.by.depot.success', createAction(
            'Get Active Shipments For Success',
            props<{ items: IDtoShipment[] }>()
        ));
        this.entityActions.add('get.active.by.depot.failure', createAction(
            'Get Active Shipments For Depot Failure',
            props<{ error: any }>()
        ));

        this.entityActions.add('get.returns.by.depot', createAction(
            'Get Shipments To Return For Depot',
            props<{ depotNumber: string, includeInDelivery: boolean }>()
        ));
        this.entityActions.add('get.returns.by.depot.success', createAction(
            'Get Shipments To Return For Depot Success',
            props<{ items: IDtoShipment[] }>()
        ));
        this.entityActions.add('get.returns.by.depot.failure', createAction(
            'Get Shipments To Return For Depot Failure',
            props<{ error: any }>()
        ));
    }

    protected override doCreateEffects(): void {
        super.doCreateEffects();

        this.entityEffects.add('get.by.date', {
            effect: (actions$: Actions, dataServiceAccessor: EntityDataServiceAccessorService) => {
                return createEffect(() =>
                    actions$.pipe(
                        ofType(this.getAction('get.by.date')),
                        map(action => action),
                        switchMap(action =>
                            (dataServiceAccessor.getEntityDataService<IDtoShipment>(this.dataAccessName) as IDtoShipmentEntityDataService)
                                .getShipmentsForDate$(action.date).pipe(
                                    map(items => this.getAction('get.by.date.success')({ items: items })),
                                    catchError(error => of(this.getAction('get.by.date.failure')({ error: error })))
                                )
                        )
                    )
                );
            }
        });

        this.entityEffects.add('get.active.by.user', {
            effect: (actions$: Actions, dataServiceAccessor: EntityDataServiceAccessorService) => {
                return createEffect(() =>
                    actions$.pipe(
                        ofType(this.getAction('get.active.by.user')),
                        map(action => action),
                        switchMap(action =>
                            (dataServiceAccessor.getEntityDataService<IDtoShipment>(this.dataAccessName) as IDtoShipmentEntityDataService)
                                .getActiveShipmentsForUser$(action.userName).pipe(
                                    map(items => this.getAction('get.active.by.user.success')({ items: items })),
                                    catchError(error => of(this.getAction('get.active.by.user.failure')({ error: error })))
                                )
                        )
                    )
                );
            }
        });

        this.entityEffects.add('get.active.by.depot', {
            effect: (actions$: Actions, dataServiceAccessor: EntityDataServiceAccessorService) => {
                return createEffect(() =>
                    actions$.pipe(
                        ofType(this.getAction('get.active.by.depot')),
                        map(action => action),
                        switchMap(action =>
                            (dataServiceAccessor.getEntityDataService<IDtoShipment>(this.dataAccessName) as IDtoShipmentEntityDataService)
                                .getActiveShipmentsForDepot$(action.depotNumber).pipe(
                                    map(items => this.getAction('get.active.by.depot.success')({ items: items })),
                                    catchError(error => of(this.getAction('get.active.by.depot.failure')({ error: error })))
                                )
                        )
                    )
                );
            }
        });

        this.entityEffects.add('get.returns.by.depot', {
            effect: (actions$: Actions, dataServiceAccessor: EntityDataServiceAccessorService) => {
                return createEffect(() =>
                    actions$.pipe(
                        ofType(this.getAction('get.returns.by.depot')),
                        map(action => action),
                        switchMap(action =>
                            (dataServiceAccessor.getEntityDataService<IDtoShipment>(this.dataAccessName) as IDtoShipmentEntityDataService)
                                .getShipmentsToReturnForDepot$(action.depotNumber, action.includeInDelivery).pipe(
                                    map(items => this.getAction('get.returns.by.depot.success')({ items: items })),
                                    catchError(error => of(this.getAction('get.returns.by.depot.failure')({ error: error })))
                                )
                        )
                    )
                );
            }
        });
    }

    protected override doCreateSelectors() {
        super.doCreateSelectors();
    }

    protected override doGetReducers<S extends IGenericEntityState<IDtoShipment>>(): any {
        const result: any = super.doGetReducers();

        result.push(
            on(
                this.getAction('get.by.date'),
                this.getAction('get.active.by.user'),
                this.getAction('get.active.by.depot'),
                this.getAction('get.returns.by.depot'),
                (state: any, { showLoadingIndicator }) => {
                    return {
                        ...state,
                        loading: ((showLoadingIndicator === true) ? true : state.loading)
                    };
                }
            )
        );
        result.push(
            on(
                this.getAction('get.by.date.failure'),
                this.getAction('get.active.by.user.failure'),
                this.getAction('get.active.by.depot.failure'),
                this.getAction('get.returns.by.depot.failure'),
                (state: any) => {
                    return {
                        ...state,
                        loading: ((state.loading === true) ? false : state.loading)
                    };
                }
            )
        );
        result.push(
            on(
                this.getAction('get.by.date.success'),
                this.getAction('get.active.by.user.success'),
                this.getAction('get.active.by.depot.success'),
                this.getAction('get.returns.by.depot.success'),
                (state: any, { items }) => {
                    const res = this.entityAdapter.setAll(items, {
                        ...state,
                        loading: false,
                        selectedItem: null
                    });
                    return res;
                })
        );

        return result;
    }

}
